import { push } from 'connected-react-router';
import includes from 'lodash/includes';
import map from 'lodash/map';
import { connect } from 'react-redux';
import { compose } from 'recompact';

import { ANALYTICS_EVENTS } from 'src/app/analytics/analyticsEvents';
import { errorBoundary } from 'src/app/hocs';
import { medicationDetails, medicationDetailsForTransfer } from 'src/app/router/paths';
import {
  cartAddItems,
  cartRemoveItems,
  segmentAnalyticsEvent,
  setMedicationLoadingState,
  setShowNewArchivedPageModal,
} from 'src/app/state/actions';
import {
  archiveItem,
  fetchHomescreen,
  loadHomescreenMedications,
  toggleArchiveSection,
} from 'src/app/state/actions/homescreen';
import { getCartItems } from 'src/app/state/selectors';
import type { Dispatch } from 'src/app/state/store';
import { HomeItemType } from 'src/app/types';
import { HOMESCREEN_ITEM_TYPES, HomeProductStatus } from 'src/consts';

import Renderer from '.';

function makeMapStateToProps() {
  return (state, ownProps) => {
    const { homeScreenItem } = ownProps;
    // Handling archiving medications since they pull from a different spot in redux.
    const cartItems = getCartItems(state);
    const { status, type, prescriptionFulfillmentId } = homeScreenItem;

    const isInCart = includes(map(cartItems, 'fulfillmentId'), prescriptionFulfillmentId);
    const isReadyForCheckout = status === HomeProductStatus.READY_TO_CHECKOUT;
    const isPendingTransfer = type === HOMESCREEN_ITEM_TYPES.transfer;

    return {
      homeScreenItem,
      isInCart,
      isReadyForCheckout,
      isPendingTransfer,
      cart: state.checkout.cart,
    };
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchAddToCart: cartItem => {
    const { prescriptionFulfillmentId, price, patientMedicationId, name, genericName, prescriptionId } = cartItem;

    dispatch(
      cartAddItems({
        prescriptionId,
        fulfillmentId: prescriptionFulfillmentId,
        price,
        patientMedicationId,
      })
    );

    /** legacy event */
    dispatch(
      segmentAnalyticsEvent(ANALYTICS_EVENTS.medicationAddedToCart, {
        name,
        genericName,
      })
    );
  },
  dispatchRemoveFromCart: cartItem => {
    const { prescriptionFulfillmentId, price, patientMedicationId, name, genericName } = cartItem;

    dispatch(
      cartRemoveItems({
        fulfillmentId: prescriptionFulfillmentId,
        price,
        patientMedicationId,
      })
    );

    /** legacy event */
    dispatch(
      segmentAnalyticsEvent(ANALYTICS_EVENTS.medicationRemovedFromCart, {
        name,
        genericName,
      })
    );
  },
  dispatchArchive: async (patientMedicationItemId, isArchived) => {
    return await dispatch(archiveItem(patientMedicationItemId, isArchived));
  },
  dispatchGetAllHomescreenItems: async () => {
    await dispatch(loadHomescreenMedications());
    await dispatch(fetchHomescreen());
  },
  dispatchToggleArchiveSection: value => {
    dispatch(toggleArchiveSection(value));
  },
  dispatchGoToMedicationDetails: (prescriptionId: string, type: string) => {
    if (type === HomeItemType.transfer) {
      dispatch(push(medicationDetailsForTransfer.toPath({ prescriptionId })));
    } else {
      dispatch(push(medicationDetails.toPath({ prescriptionId })));
    }
  },
  dispatchSetShowNewArchivedPageModal: (showModal: boolean) => dispatch(setShowNewArchivedPageModal(showModal)),
  dispatchSetMedicationLoadingState: (patientMedicationItemId: string, isLoading: boolean) =>
    dispatch(setMedicationLoadingState(patientMedicationItemId, isLoading)),
});

export const MedicationContents = compose(errorBoundary, connect(makeMapStateToProps, mapDispatchToProps))(Renderer);
